import { useEffect, useRef, useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Camera, Eye, EyeOff} from 'lucide-react'
import PhoneInput, { PAISES, validarNumero, type PaisOpcao } from '@/components/PhoneInput'
import {
  formatarData,
  rotuloStatus,
  diasRestantes,
} from '@/utils/assinatura'

interface Props {
  usuarioLogado: string
  nomeLogado: string
}

function forcaSenha(s: string): { nivel: 'fraca' | 'media' | 'forte'; cor: string } {
  if (!s) return { nivel: 'fraca', cor: '#E5E7EB' }
  const temNum = /\d/.test(s)
  const temLetra = /[a-zA-Z]/.test(s)
  const temEspecial = /[^a-zA-Z0-9]/.test(s)
  if (s.length >= 8 && temNum && temLetra && temEspecial) return { nivel: 'forte', cor: '#2D6A4F' }
  if (s.length >= 6 && temNum && temLetra) return { nivel: 'media', cor: '#F59E0B' }
  return { nivel: 'fraca', cor: '#EF4444' }
}

const LABEL_FORCA: Record<string, string> = { fraca: 'Fraca', media: 'Média', forte: 'Forte' }

export default function MeuPerfilAba({ usuarioLogado, nomeLogado }: Props) {
  const [consultora, setConsultora] = useState<any>(null)
  const [carregando, setCarregando] = useState(true)

  // Dados pessoais
  const [nome, setNome] = useState('')
  const [tituloProfissional, setTituloProfissional] = useState('')
  const [foto, setFoto] = useState('')
  const [fotoPreview, setFotoPreview] = useState('')
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [salvandoPerfil, setSalvandoPerfil] = useState(false)
  const [sucessoPerfil, setSucessoPerfil] = useState(false)
  const [exibirOleosResultado, setExibirOleosResultado] = useState(false)
  const [exibirOleosResultadoKids, setExibirOleosResultadoKids] = useState(false)
  const [exibirOleosResultadoCorporativo, setExibirOleosResultadoCorporativo] = useState(false)
  const [salvandoConfig, setSalvandoConfig] = useState(false)
  const [salvandoConfigKids, setSalvandoConfigKids] = useState(false)
  const [salvandoConfigCorporativo, setSalvandoConfigCorporativo] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // WhatsApp
  const [whatsapp, setWhatsapp] = useState('')
  const [paisWhatsapp, setPaisWhatsapp] = useState<PaisOpcao>(PAISES[0])

  // Segurança
  const [senhaAtual, setSenhaAtual] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [showSenhaAtual, setShowSenhaAtual] = useState(false)
  const [showNovaSenha, setShowNovaSenha] = useState(false)
  const [showConfirmar, setShowConfirmar] = useState(false)
  const [trocandoSenha, setTrocandoSenha] = useState(false)
  const [feedbackSenha, setFeedbackSenha] = useState<{ ok: boolean; msg: string } | null>(null)

  const forca = forcaSenha(novaSenha)
  const senhasDiferentes = novaSenha && confirmarSenha && novaSenha !== confirmarSenha

  useEffect(() => {
    void carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioLogado])

  async function carregar() {
    setCarregando(true)
    try {
      const resp = await lumi.entities.consultoras.list({
        filter: { usuario: usuarioLogado },
        limit: 1,
      })
      const c = resp?.list?.[0]
      if (c) {
        setConsultora(c)
        setNome(c.nome || nomeLogado || '')
        setTituloProfissional(c.tituloProfissional || '')
        const fotoAtual = c.fotoPerfil || c.foto || ''
        setFoto(fotoAtual)
        setFotoPreview(fotoAtual)
        // WhatsApp — separar DDI do número salvo
        const whatsSalvo = (c.whatsapp || '').replace(/\s/g, '')
        const paisDetectado =
          PAISES.find(p => p.codigo && whatsSalvo.startsWith(p.codigo)) || PAISES[0]
        setPaisWhatsapp(paisDetectado)
        setWhatsapp(
          paisDetectado.codigo
            ? whatsSalvo.slice(paisDetectado.codigo.length).replace(/\D/g, '')
            : whatsSalvo.replace(/\D/g, '')
        )
        setExibirOleosResultado(c.exibirOleosResultado === true)
        setExibirOleosResultadoKids(c.exibirOleosResultadoKids === true)
        setExibirOleosResultadoCorporativo(c.exibirOleosResultadoCorporativo === true)
      }
    } catch (e) {
      console.error('[perfil] erro ao carregar', e)
    } finally {
      setCarregando(false)
    }
  }

  async function handleFotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. O tamanho máximo é 5MB.')
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    const nomeArquivo = (file.name || '').toLowerCase()
    const tipoOk =
      ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(
        file.type
      ) || /\.(jpe?g|png|webp|heic|heif)$/.test(nomeArquivo)
    if (!tipoOk) {
      toast.error('Formatos aceitos: JPG, PNG, HEIC ou WEBP')
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    setUploadingFoto(true)
    try {
      const result = await lumi.tools.file.upload([file])
      const fileUrl = result?.[0]?.fileUrl || ''
      if (fileUrl) {
        // Cache-busting para forçar atualização visual imediata do preview
        const urlComCache = fileUrl.includes('?') ? `${fileUrl}&t=${Date.now()}` : `${fileUrl}?t=${Date.now()}`
        setFoto(fileUrl)
        setFotoPreview(urlComCache)
        toast.success('Foto carregada! Clique em salvar para confirmar.')
      } else {
        const uploadError = result?.[0]?.uploadError || 'Erro desconhecido'
        console.error('[perfil] upload error:', uploadError)
        toast.error(`Não foi possível fazer o upload da foto: ${uploadError}`)
      }
    } catch (err) {
      console.error('[perfil] erro ao fazer upload', err)
      toast.error('Erro ao enviar foto. Tente novamente.')
    } finally {
      setUploadingFoto(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function salvarDadosPessoais(e: React.FormEvent) {
    e.preventDefault()
    if (!nome.trim()) { toast.error('O nome é obrigatório'); return }
    if (!consultora) { toast.error('Perfil não encontrado'); return }
    // Validar WhatsApp (obrigatório)
    const validacaoWhats = validarNumero(whatsapp, paisWhatsapp)
    if (!validacaoWhats.valido) {
      toast.error(`WhatsApp inválido. ${validacaoWhats.mensagem}`)
      return
    }
    const whatsappCompleto = `${paisWhatsapp.codigo}${whatsapp.replace(/\D/g, '')}`
    setSalvandoPerfil(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        nome: nome.trim(),
        tituloProfissional: tituloProfissional.trim(),
        whatsapp: whatsappCompleto,
        pais: paisWhatsapp.nomeSalvo,
        fotoPerfil: foto,
        foto,
        updatedAt: new Date().toISOString(),
      })
      setConsultora({ ...consultora, fotoPerfil: foto, foto, whatsapp: whatsappCompleto })
      setSucessoPerfil(true)
      setTimeout(() => setSucessoPerfil(false), 3000)
      toast.success('✅ Perfil atualizado com sucesso!')
    } catch (err) {
      console.error('[perfil] erro ao salvar', err)
      toast.error('Erro ao salvar perfil. Tente novamente.')
    } finally {
      setSalvandoPerfil(false)
    }
  }

  async function salvarExibirOleos(valor: boolean) {
    if (!consultora?._id) {
      toast.error('Perfil não encontrado')
      return
    }
    const anterior = exibirOleosResultado
    setExibirOleosResultado(valor)
    setSalvandoConfig(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        exibirOleosResultado: valor,
        updatedAt: new Date().toISOString(),
      })
      setConsultora({ ...consultora, exibirOleosResultado: valor })
      toast.success(valor ? 'Os óleos serão exibidos no resultado.' : 'Os óleos ficarão ocultos no resultado público.')
    } catch (err) {
      console.error('[perfil] erro ao salvar configuração', err)
      setExibirOleosResultado(anterior)
      toast.error('Erro ao salvar configuração. Tente novamente.')
    } finally {
      setSalvandoConfig(false)
    }
  }

  async function salvarExibirOleosCorporativo(valor: boolean) {
    if (!consultora?._id) {
      toast.error('Perfil não encontrado')
      return
    }
    const anterior = exibirOleosResultadoCorporativo
    setExibirOleosResultadoCorporativo(valor)
    setSalvandoConfigCorporativo(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        exibirOleosResultadoCorporativo: valor,
        updatedAt: new Date().toISOString(),
      })
      setConsultora({ ...consultora, exibirOleosResultadoCorporativo: valor })
      toast.success(valor ? 'Os óleos serão exibidos no resultado corporativo.' : 'Os óleos ficarão ocultos no resultado corporativo.')
    } catch (err) {
      console.error('[perfil] erro ao salvar configuração corporativo', err)
      setExibirOleosResultadoCorporativo(anterior)
      toast.error('Erro ao salvar configuração. Tente novamente.')
    } finally {
      setSalvandoConfigCorporativo(false)
    }
  }

  async function salvarExibirOleosKids(valor: boolean) {
    if (!consultora?._id) {
      toast.error('Perfil não encontrado')
      return
    }
    const anterior = exibirOleosResultadoKids
    setExibirOleosResultadoKids(valor)
    setSalvandoConfigKids(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        exibirOleosResultadoKids: valor,
        updatedAt: new Date().toISOString(),
      })
      setConsultora({ ...consultora, exibirOleosResultadoKids: valor })
      toast.success(valor ? 'Os óleos serão exibidos no resultado kids.' : 'Os óleos ficarão ocultos no resultado kids.')
    } catch (err) {
      console.error('[perfil] erro ao salvar configuração kids', err)
      setExibirOleosResultadoKids(anterior)
      toast.error('Erro ao salvar configuração. Tente novamente.')
    } finally {
      setSalvandoConfigKids(false)
    }
  }

  async function trocarSenha(e: React.FormEvent) {
    e.preventDefault()
    setFeedbackSenha(null)
    if (!senhaAtual) { toast.error('Informe a senha atual'); return }
    if (novaSenha.length < 6) { toast.error('A nova senha deve ter pelo menos 6 caracteres'); return }
    if (!/\d/.test(novaSenha)) { toast.error('A nova senha deve conter pelo menos 1 número'); return }
    if (novaSenha !== confirmarSenha) { toast.error('As senhas não coincidem'); return }
    if (!consultora) { toast.error('Perfil não encontrado'); return }

    if (consultora.senha !== senhaAtual) {
      setFeedbackSenha({ ok: false, msg: '❌ Senha atual incorreta. Tente novamente.' })
      return
    }

    setTrocandoSenha(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        senha: novaSenha,
        updatedAt: new Date().toISOString(),
      })
      setFeedbackSenha({ ok: true, msg: '✅ Senha alterada com sucesso!' })
      setSenhaAtual('')
      setNovaSenha('')
      setConfirmarSenha('')
      // Atualizar sessão local
      const sessao = JSON.parse(localStorage.getItem('admin2_sessao') || '{}')
      localStorage.setItem('admin2_sessao', JSON.stringify({ ...sessao, _senhaAtualizada: true }))
    } catch (err) {
      console.error('[perfil] erro ao trocar senha', err)
      setFeedbackSenha({ ok: false, msg: '❌ Erro ao alterar senha. Tente novamente.' })
    } finally {
      setTrocandoSenha(false)
    }
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6A4F]" />
      </div>
    )
  }

  // Assinatura
  const tipoAcesso = consultora?.tipoAcesso || ''
  const statusAssinatura = consultora?.statusAssinatura || ''
  const exibirAssinatura = tipoAcesso === 'mensal' || tipoAcesso === 'anual'
  const diasRestantesAssin = diasRestantes(consultora?.dataExpiracao)

  const nomeInicial = (nome || nomeLogado || '?').charAt(0).toUpperCase()

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-1">Meu Perfil</h2>
        <p className="text-sm text-[#5F5E5A]">Gerencie seus dados, foto e configurações de acesso.</p>
      </div>

      {/* ══════════════════════════════════════ */}
      {/* SEÇÃO 1 — DADOS PESSOAIS              */}
      {/* ══════════════════════════════════════ */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-5">Dados Pessoais</h3>

        <form onSubmit={salvarDadosPessoais} className="space-y-5">
          {/* Foto de perfil */}
          <div>
            <label className="block text-sm font-medium text-[#5F5E5A] mb-2">Sua foto de perfil</label>
            <div className="flex items-center gap-5">
              {/* Avatar preview */}
              <div className="relative flex-shrink-0">
                {fotoPreview ? (
                  <img
                    src={fotoPreview}
                    alt="Foto de perfil"
                    className="w-20 h-20 rounded-full object-cover border-2 border-[#A8DFC4]"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#2D6A4F] border-2 border-[#A8DFC4] flex items-center justify-center text-white text-2xl font-bold">
                    {nomeInicial}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingFoto}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-[#2D6A4F] rounded-full flex items-center justify-center hover:bg-[#245A42] transition-colors disabled:opacity-60"
                  title="Alterar foto"
                >
                  <Camera className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingFoto}
                  className="px-4 py-2 border-2 border-[#EDE0D4] rounded-xl text-sm text-[#5F5E5A] hover:border-[#A8DFC4] transition-colors disabled:opacity-60"
                >
                  {uploadingFoto ? 'Enviando...' : 'Escolher foto'}
                </button>
                <p className="text-xs text-[#B08968] mt-1.5">
                  Recomendamos uma foto profissional com boa iluminação. Tamanho ideal: 400×400px
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                className="hidden"
                onChange={handleFotoChange}
              />
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-[#5F5E5A] mb-1.5">Nome completo *</label>
            <input
              type="text"
              required
              value={nome}
              onChange={e => setNome(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
            />
          </div>

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-[#5F5E5A] mb-1.5">Seu título profissional</label>
            <input
              type="text"
              maxLength={100}
              value={tituloProfissional}
              onChange={e => setTituloProfissional(e.target.value)}
              placeholder="Ex: Aromaterapeuta e Consultora de Bem-Estar"
              className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
            />
            <p className="text-xs text-[#B08968] mt-1">Aparece abaixo do seu nome no diagnóstico.</p>
          </div>

          {/* WhatsApp */}
          <div>
            <label className="block text-sm font-medium text-[#5F5E5A] mb-1.5">Seu WhatsApp *</label>
            <PhoneInput
              value={whatsapp}
              paisInicial={paisWhatsapp}
              onChange={(numero, pais) => {
                setWhatsapp(numero)
                setPaisWhatsapp(pais)
              }}
              required
            />
            <p className="text-xs text-[#B08968] mt-1">
              Usado para contato com seus leads. Não aparece publicamente no diagnóstico.
            </p>
          </div>

          {sucessoPerfil && (
            <div className="bg-[#D8F3DC] border border-[#A8DFC4] rounded-xl px-4 py-3 text-sm text-[#2D6A4F] font-medium">
              ✅ Perfil atualizado com sucesso!
            </div>
          )}

          <button
            type="submit"
            disabled={salvandoPerfil}
            className="w-full bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-[#245A42] transition-all disabled:opacity-60"
          >
            {salvandoPerfil ? 'Salvando...' : 'Salvar dados pessoais'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-1">Configurações do diagnóstico</h3>
        <div style={{ height: 1, backgroundColor: '#A8DFC4', margin: '12px 0 20px' }} />
        <p className="text-sm font-medium text-[#2C2C2A] mb-1">Exibir óleos no resultado</p>
        <p className="text-xs text-[#B08968] mb-4">
          Quando estiver em Não, a cliente vê só o mapa de saúde física e emocional. Os óleos continuam salvos no seu painel.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={salvandoConfig}
            onClick={() => { if (!exibirOleosResultado) void salvarExibirOleos(true) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              exibirOleosResultado
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Sim
          </button>
          <button
            type="button"
            disabled={salvandoConfig}
            onClick={() => { if (exibirOleosResultado) void salvarExibirOleos(false) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              !exibirOleosResultado
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Não
          </button>
        </div>
        {salvandoConfig ? (
          <p className="text-xs text-[#B08968] mt-3">Salvando configuração...</p>
        ) : null}

        <p className="text-sm font-medium text-[#2C2C2A] mb-1 mt-8">Exibir óleos no resultado kids</p>
        <p className="text-xs text-[#B08968] mb-4">
          Quando estiver em Não, a mãe não vê a lista nem as explicações dos óleos no diagnóstico infantil. Os óleos continuam salvos no seu painel.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={salvandoConfigKids}
            onClick={() => { if (!exibirOleosResultadoKids) void salvarExibirOleosKids(true) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              exibirOleosResultadoKids
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Sim
          </button>
          <button
            type="button"
            disabled={salvandoConfigKids}
            onClick={() => { if (exibirOleosResultadoKids) void salvarExibirOleosKids(false) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              !exibirOleosResultadoKids
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Não
          </button>
        </div>
        {salvandoConfigKids ? (
          <p className="text-xs text-[#B08968] mt-3">Salvando configuração...</p>
        ) : null}

        <p className="text-sm font-medium text-[#2C2C2A] mb-1 mt-8">Exibir óleos no resultado corporativo</p>
        <p className="text-xs text-[#B08968] mb-4">
          Quando estiver em Não, a pessoa não vê a lista nem as explicações dos óleos no diagnóstico corporativo. Os óleos continuam salvos no seu painel.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={salvandoConfigCorporativo}
            onClick={() => { if (!exibirOleosResultadoCorporativo) void salvarExibirOleosCorporativo(true) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              exibirOleosResultadoCorporativo
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Sim
          </button>
          <button
            type="button"
            disabled={salvandoConfigCorporativo}
            onClick={() => { if (exibirOleosResultadoCorporativo) void salvarExibirOleosCorporativo(false) }}
            className={`flex-1 py-3 rounded-full text-sm font-medium border-2 transition-all disabled:opacity-60 ${
              !exibirOleosResultadoCorporativo
                ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                : 'bg-white text-[#5F5E5A] border-[#EDE0D4] hover:border-[#A8DFC4]'
            }`}
          >
            Não
          </button>
        </div>
        {salvandoConfigCorporativo ? (
          <p className="text-xs text-[#B08968] mt-3">Salvando configuração...</p>
        ) : null}
      </div>

      {/* ══════════════════════════════════════ */}
      {/* SEÇÃO 2 — SEGURANÇA                   */}
      {/* ══════════════════════════════════════ */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-1">Segurança</h3>
        <div style={{ height: 1, backgroundColor: '#A8DFC4', margin: '12px 0 20px' }} />

        <form onSubmit={trocarSenha} className="space-y-4">
          <p className="text-sm font-medium text-[#2C2C2A] mb-2">Alterar senha</p>

          {/* Senha atual */}
          <div>
            <label className="block text-sm text-[#5F5E5A] mb-1.5">Senha atual</label>
            <div className="relative">
              <input
                type={showSenhaAtual ? 'text' : 'password'}
                value={senhaAtual}
                onChange={e => setSenhaAtual(e.target.value)}
                className="w-full px-4 py-3 pr-11 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
              />
              <button type="button" onClick={() => setShowSenhaAtual(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B08968]">
                {showSenhaAtual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Nova senha */}
          <div>
            <label className="block text-sm text-[#5F5E5A] mb-1.5">Nova senha</label>
            <div className="relative">
              <input
                type={showNovaSenha ? 'text' : 'password'}
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                className="w-full px-4 py-3 pr-11 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-[#2C2C2A]"
              />
              <button type="button" onClick={() => setShowNovaSenha(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B08968]">
                {showNovaSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {novaSenha && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex gap-1 flex-1">
                  {(['fraca', 'media', 'forte'] as const).map(n => (
                    <div
                      key={n}
                      className="h-1.5 flex-1 rounded-full"
                      style={{
                        backgroundColor:
                          (forca.nivel === 'fraca' && n === 'fraca') ||
                          (forca.nivel === 'media' && (n === 'fraca' || n === 'media')) ||
                          (forca.nivel === 'forte')
                            ? forca.cor
                            : '#E5E7EB',
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs" style={{ color: forca.cor }}>
                  {LABEL_FORCA[forca.nivel]}
                </span>
              </div>
            )}
            <p className="text-xs text-[#B08968] mt-1">Mínimo 6 caracteres e pelo menos 1 número.</p>
          </div>

          {/* Confirmar */}
          <div>
            <label className="block text-sm text-[#5F5E5A] mb-1.5">Confirmar nova senha</label>
            <div className="relative">
              <input
                type={showConfirmar ? 'text' : 'password'}
                value={confirmarSenha}
                onChange={e => setConfirmarSenha(e.target.value)}
                className={`w-full px-4 py-3 pr-11 border-2 rounded-xl focus:outline-none text-[#2C2C2A] ${senhasDiferentes ? 'border-red-400 focus:border-red-400' : 'border-[#EDE0D4] focus:border-[#52B788]'}`}
              />
              <button type="button" onClick={() => setShowConfirmar(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B08968]">
                {showConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {senhasDiferentes && (
              <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
            )}
          </div>

          {feedbackSenha && (
            <div
              className={`px-4 py-3 rounded-xl text-sm font-medium ${feedbackSenha.ok ? 'bg-[#D8F3DC] text-[#2D6A4F] border border-[#A8DFC4]' : 'bg-red-50 text-red-700 border border-red-200'}`}
            >
              {feedbackSenha.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={trocandoSenha || !!senhasDiferentes}
            className="w-full bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-[#245A42] transition-all disabled:opacity-60"
          >
            {trocandoSenha ? 'Alterando...' : 'Alterar senha'}
          </button>
        </form>
      </div>

      {/* ══════════════════════════════════════ */}
      {/* SEÇÃO 3 — ASSINATURA (apenas pago)    */}
      {/* ══════════════════════════════════════ */}
      {exibirAssinatura && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-1">Minha Assinatura</h3>
          <div style={{ height: 1, backgroundColor: '#A8DFC4', margin: '12px 0 20px' }} />

          <div
            className="rounded-xl px-5 py-4 mb-5"
            style={{ backgroundColor: '#D8F3DC', border: '1.5px solid #A8DFC4' }}
          >
            <p className="text-sm text-[#2D6A4F]">
              Seu plano atual:{' '}
              <strong className="capitalize">{tipoAcesso}</strong>
            </p>
            <p className="text-sm text-[#2D6A4F] mt-0.5">
              Status:{' '}
              <strong>
                {statusAssinatura === 'ativo' ? '✅ Ativo' : rotuloStatus(statusAssinatura)}
              </strong>
            </p>
            {consultora?.dataExpiracao && (
              <p className="text-sm text-[#2D6A4F] mt-0.5">
                Próxima cobrança / vencimento:{' '}
                <strong>{formatarData(consultora.dataExpiracao)}</strong>
              </p>
            )}
          </div>

          {/* Upgrade para anual se for mensal */}
          {tipoAcesso === 'mensal' && (
            <div className="mb-5">
              <a
                href="/assinar"
                className="block w-full text-center bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-[#245A42] transition-all"
              >
                Fazer upgrade para anual
              </a>
              <p className="text-xs text-[#B08968] text-center mt-1.5">Assinatura anual · pague uma única vez</p>
            </div>
          )}

          {/* Alertas de vencimento */}
          {diasRestantesAssin !== null && diasRestantesAssin < 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
              <p className="text-sm text-red-700 font-medium">❌ Sua assinatura está vencida. Renove para recuperar o acesso.</p>
              <a href="/renovar" className="inline-block mt-2 text-sm text-white bg-red-600 px-4 py-1.5 rounded-full hover:bg-red-700 transition-colors font-medium">Renovar agora</a>
            </div>
          )}
          {diasRestantesAssin !== null && diasRestantesAssin >= 0 && diasRestantesAssin < 7 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-xl px-4 py-3 mb-4">
              <p className="text-sm text-yellow-800 font-medium">
                ⚠️ Sua assinatura vence em {diasRestantesAssin} dia(s). Renove para não perder o acesso.
              </p>
              <a href="/renovar" className="inline-block mt-2 text-sm text-white bg-[#2D6A4F] px-4 py-1.5 rounded-full hover:bg-[#245A42] transition-colors font-medium">Renovar agora</a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
